import { forwardRef } from "react";
import * as RS from "@radix-ui/react-switch";
import clsx from "clsx";

export type SwitchToggleProps = {
  label?: string;
  hint?: string;
  size?: "sm" | "md";
} & Omit<RS.SwitchProps, "size">;

const sizeMap = {
  sm: { root: "w-8 h-4", thumb: "size-3 data-[state=checked]:translate-x-4" },
  md: { root: "w-10 h-5", thumb: "size-4 data-[state=checked]:translate-x-5" },
};

export const SwitchToggle = forwardRef<HTMLButtonElement, SwitchToggleProps>(function SwitchToggle(
  { label, hint, size = "md", className, id, ...rest },
  ref,
) {
  const cid = id ?? `sw-${Math.random().toString(36).slice(2, 8)}`;
  const s = sizeMap[size];
  return (
    <label htmlFor={cid} className="inline-flex items-center gap-3 text-sm cursor-pointer">
      <RS.Root
        ref={ref}
        id={cid}
        className={clsx(
          "relative inline-flex shrink-0 items-center rounded-full transition-colors px-0.5",
          "bg-[var(--color-border-default)] data-[state=checked]:bg-[var(--color-accent)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-1",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          s.root,
          className,
        )}
        {...rest}
      >
        <RS.Thumb
          className={clsx(
            "rounded-full bg-[var(--color-surface-base)] shadow-sm transition-transform",
            s.thumb,
          )}
        />
      </RS.Root>
      {(label || hint) && (
        <span className="flex flex-col gap-0.5">
          {label && <span className="text-[var(--color-text-primary)]">{label}</span>}
          {hint && <span className="text-xs text-[var(--color-text-secondary)]">{hint}</span>}
        </span>
      )}
    </label>
  );
});
